import type { ApiError, NormalizedErrorDetail } from './apiError.types';
import type { ErrorTranslationOptions } from './errorUtils.types';

/**
 * Label key to i18n key mapping strategy:
 * 1. Try direct mapping: errors.{labelKey}
 * 2. Try field-specific mapping: errors.fields.{field}.{labelKey}
 * 3. Try common error mapping: errors.common.{labelKey}
 * 4. Fallback to message from backend
 *
 * @param error - Normalized error detail to translate
 * @param options - Translation options
 * @returns Translated error message
 */
export function translateError(
  error: NormalizedErrorDetail,
  options: ErrorTranslationOptions,
): string {
  const { i18n, namespace = 'errors', useFallback = true } = options;

  if (!error.labelKey) {
    return error.message;
  }

  // Strategy 1: Direct mapping
  const directKey = `${namespace}:${error.labelKey}`;
  if (i18n.exists(directKey)) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return i18n.t(directKey as any, error.params);
  }

  // Strategy 2: Field-specific mapping (if field is present)
  if (error.field) {
    const fieldKey = `${namespace}:fields.${error.field}.${error.labelKey}`;
    if (i18n.exists(fieldKey)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return i18n.t(fieldKey as any, error.params);
    }
  }

  // Strategy 3: Common error mapping
  const commonKey = `${namespace}:common.${error.labelKey}`;
  if (i18n.exists(commonKey)) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return i18n.t(commonKey as any, error.params);
  }

  // Strategy 4: Fallback to backend message
  if (useFallback) {
    return error.message;
  }

  return error.labelKey;
}

/**
 * Translates all errors in an ApiError object
 *
 * @param apiError - API error to translate
 * @param options - Translation options
 * @returns ApiError with translated messages
 */
export function translateApiError(
  apiError: ApiError,
  options: ErrorTranslationOptions,
): ApiError {
  return {
    ...apiError,
    errors: apiError.errors.map((error) => ({
      ...error,
      message: translateError(error, options),
    })),
  };
}

/**
 * Gets all error messages from ApiError (translated if options provided)
 *
 * @param apiError - API error to extract messages from
 * @param options - Optional translation options
 * @returns Array of error messages
 */
export function getErrorMessages(
  apiError: ApiError,
  options?: ErrorTranslationOptions,
): string[] {
  if (options) {
    const translated = translateApiError(apiError, options);
    return translated.errors.map((e) => e.message);
  }
  return apiError.errors.map((e) => e.message);
}

/**
 * Gets first error message (translated if options provided)
 *
 * @param apiError - API error to extract message from
 * @param options - Optional translation options
 * @returns First error message or fallback
 */
export function getFirstErrorMessage(
  apiError: ApiError,
  options?: ErrorTranslationOptions,
): string {
  const messages = getErrorMessages(apiError, options);
  if (messages[0]) return messages[0];
  return options?.t
    ? options.t('common.UnexpectedError' as any) // eslint-disable-line @typescript-eslint/no-explicit-any
    : 'common.UnexpectedError';
}
